import type { CSSProperties } from 'react';
import type { BadgeImageSizeEntry } from '@/helpers/utils/productBadging';

export type ViewportTier = 'desktop' | 'tablet' | 'mobile';

export type BadgeImageLayoutResolved =
  | { kind: 'none' }
  | { kind: 'widthLead'; width: number }
  | { kind: 'heightLead'; height: number }
  | { kind: 'fixed'; width: number; height: number };

export function viewportTierFromInnerWidth(innerWidth: number): ViewportTier {
  if (innerWidth >= 1024) return 'desktop';
  if (innerWidth >= 768) return 'tablet';
  return 'mobile';
}

function normalizeEntryType(t: unknown): string {
  return String(t ?? '')
    .trim()
    .toLowerCase();
}

function typeMatchesTier(rawType: unknown, tier: ViewportTier): boolean {
  const t = normalizeEntryType(rawType);
  if (!t) return false;
  if (tier === 'desktop') {
    return t === 'desktop' || t === 'lg' || t === 'large';
  }
  if (tier === 'tablet') {
    return t === 'tablet' || t === 'md' || t === 'ipad';
  }
  return t === 'mobile' || t === 'sm' || t === 'xs' || t === 'phone';
}

/**
 * Prefer a row whose `Type` / `type` matches the current viewport tier; else first row with no type; else first row.
 */
export function pickBadgeImageSizeRow(
  imageSizes: BadgeImageSizeEntry[] | null | undefined,
  innerWidth: number,
): BadgeImageSizeEntry | null {
  if (!Array.isArray(imageSizes) || imageSizes.length === 0) return null;
  const tier = viewportTierFromInnerWidth(innerWidth);
  const tierMatch = imageSizes.find((row) => typeMatchesTier(row.Type ?? row.type, tier));
  if (tierMatch) return tierMatch;
  const untyped = imageSizes.find((row) => !normalizeEntryType(row.Type ?? row.type));
  if (untyped) return untyped;
  return imageSizes[0] ?? null;
}

function numericDimension(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  if (typeof v === 'string' && v.trim()) {
    const n = parseFloat(v.trim());
    return Number.isFinite(n) && n > 0 ? n : 0;
  }
  return 0;
}

/** Reads `width` and `height` / CMS typo `Heigth`. Non-positive values mean “not set”. */
export function readBadgeImageSizeDimensions(row: BadgeImageSizeEntry): { w: number; h: number } {
  const w = numericDimension(row.width);
  const h = numericDimension(row.height ?? row.Heigth);
  return { w, h };
}

/**
 * width only & height 0 → width-led (height follows asset).
 * height only & width 0 → height-led (width follows asset).
 * both set → fixed box.
 */
export function resolveBadgeImageLayoutFromRow(row: BadgeImageSizeEntry | null): BadgeImageLayoutResolved {
  if (!row) return { kind: 'none' };
  const { w, h } = readBadgeImageSizeDimensions(row);
  if (w > 0 && h <= 0) return { kind: 'widthLead', width: w };
  if (h > 0 && w <= 0) return { kind: 'heightLead', height: h };
  if (w > 0 && h > 0) return { kind: 'fixed', width: w, height: h };
  return { kind: 'none' };
}

/**
 * Styles for the **background image layer only** (not the whole badge pill: overlays, padding, and text
 * chrome stay separate). Apply this to a wrapper immediately around `<img>` / image component.
 */
export function mergeImageSizesImageBoxStyle(layout: BadgeImageLayoutResolved, isCircle: boolean): CSSProperties {
  if (layout.kind === 'none') return {};
  if (layout.kind === 'fixed') {
    return {
      width: layout.width,
      height: layout.height,
      minWidth: layout.width,
      minHeight: layout.height,
    };
  }
  if (layout.kind === 'widthLead') {
    const w = layout.width;
    if (isCircle) {
      return { width: w, height: w, minWidth: w, minHeight: w };
    }
    return { width: w, minHeight: 0, height: 'auto' };
  }
  const he = layout.height;
  if (isCircle) {
    return { width: he, height: he, minWidth: he, minHeight: he };
  }
  return { height: he, width: 'auto', minWidth: 0 };
}

/** CDN `width=` request size from on-screen badge dimensions. */
export function cdnWidthForBadgeLayout(layout: BadgeImageLayoutResolved): number {
  if (layout.kind === 'none') return 240;
  if (layout.kind === 'fixed')
    return Math.min(800, Math.max(64, Math.round(Math.max(layout.width, layout.height) * 2)));
  if (layout.kind === 'widthLead') return Math.min(800, Math.max(64, Math.round(layout.width * 2)));
  return Math.min(800, Math.max(64, Math.round(layout.height * 2)));
}

export function buildCmsImageUrl(
  src: string,
  widthParam: number,
  projectSettings: { imageHost?: string; webpEnabled?: boolean } | undefined,
  quality = 75,
): string {
  if (!src) return '';
  const hasAutoWebp = src.includes('auto=webp');
  const host = projectSettings?.imageHost ?? '';
  const finalSRC = src.match(/^https:\/\/images\.contentstack\.io([^'"]*)/)
    ? src.replace(/^https:\/\/images\.contentstack\.io([^'"]*)/, host + '$1')
    : src.match(/^http/)
    ? src
    : host + src;

  const webpEnabled = projectSettings?.webpEnabled ?? false;
  const webpParam = webpEnabled && !hasAutoWebp ? '&auto=webp' : '';
  const dprParam = '&dpr=1.5';
  const qualityParam = `&quality=${quality}`;
  const w = Math.max(1, Math.round(widthParam));

  if (finalSRC.indexOf('?') > -1) {
    return `${finalSRC}${qualityParam}&width=${w}${webpParam}${dprParam}`;
  }
  return `${finalSRC}?width=${w}${qualityParam}${webpParam}${dprParam}`;
}
